import { styled, css } from '../../../../modules';
import { keyframes } from 'styled-components';

export const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    background-color: #ffffff;
`;

export const LogoSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const LogoImage = styled.img`
    max-height: 48px;
    max-width: 200px;
    object-fit: contain;
`;

export const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    padding: 32px 24px 24px;
    gap: 20px;
`;

export const Title = styled.h1<{ $color: string }>`
    font-size: 22px;
    font-weight: 600;
    color: ${({ $color }) => $color};
    margin: 0;
    text-align: center;
    line-height: 1.35;
`;

export const Subtitle = styled.p`
    font-size: 14px;
    color: #555;
    margin: 0;
    text-align: center;
    line-height: 1.6;
`;

export const FormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const InputLabel = styled.span`
    font-size: 13px;
    color: #888;
`;

export const OTPRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    justify-content: center;
`;

export const OTPBox = styled.input<{ $hasError: boolean }>`
    width: 44px;
    height: 52px;
    flex-shrink: 0;
    border: 1.5px solid ${({ $hasError }) => ($hasError ? '#e53e3e' : '#ddd')};
    border-radius: 8px;
    background-color: #f7f7f7;
    font-size: 20px;
    font-weight: 600;
    text-align: center;
    outline: none;
    color: #333;
    font-family: inherit;
    caret-color: transparent;
    box-sizing: border-box;

    &:focus {
        border-color: ${({ $hasError }) => ($hasError ? '#e53e3e' : '#aaa')};
        background-color: #f0f0f0;
    }
`;

export const ErrorText = styled.p`
    font-size: 12px;
    color: #e53e3e;
    margin: -8px 0 0;
`;

export const ResendWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

export const ResendText = styled.p`
    font-size: 13px;
    color: #555;
    margin: 0;
    line-height: 1.5;
    flex: 1;
`;

export const ResendLink = styled.span<{ $color: string; $disabled: boolean }>`
    color: ${({ $color, $disabled }) => ($disabled ? '#aaa' : $color)};
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    text-decoration: underline;
    font-weight: 500;
`;

export const BlockedBadge = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    font-size: 13px;
    color: #888;
    line-height: 1.4;
    text-align: right;
`;

export const Footer = styled.footer`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 16px 24px 20px;
    border-top: 1px solid #e5e5e5;
    flex-shrink: 0;
`;

export const EditLink = styled.span<{ $color: string }>`
    color: ${({ $color }) => $color};
    cursor: pointer;
    text-decoration: underline;
    font-weight: 500;
    font-size: 13px;
    white-space: nowrap;
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const fadeOut = keyframes`
    from { opacity: 1; }
    to   { opacity: 0; }
`;

const slideUp = keyframes`
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
`;

const slideDown = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(100%); }
`;

export const ModalOverlay = styled.div<{ $closing?: boolean }>`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${fadeOut} 0.3s ease forwards;`
            : css`animation: ${fadeIn} 0.3s ease forwards;`}
`;

export const ModalSheet = styled.div<{ $closing?: boolean }>`
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 12px 24px 36px;
    width: 100%;
    max-width: calc(100vh * 3 / 4);
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${({ $closing }) =>
        $closing
            ? css`animation: ${slideDown} 0.3s ease forwards;`
            : css`animation: ${slideUp} 0.3s ease forwards;`}
`;

export const SheetHandle = styled.div`
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background-color: #ddd;
    align-self: center;
    margin-bottom: 4px;
`;

export const SheetTitle = styled.h3<{ $color: string }>`
    font-size: 18px;
    font-weight: 700;
    color: ${({ $color }) => $color};
    margin: 0;
`;

export const RadioGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const RadioGroupLabel = styled.span`
    font-size: 13px;
    color: #666;
    font-weight: 500;
`;

export const RadioOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #444;
    cursor: pointer;
`;

export const RadioInput = styled.input`
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex-shrink: 0;
`;

export const SheetButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
`;

export const UpdateButton = styled.button<{ $color: string; $disabled: boolean }>`
    width: 100%;
    padding: 15px 20px;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $color, $disabled }) => ($disabled ? '#bbb' : $color)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;

    &:active {
        opacity: ${({ $disabled }) => ($disabled ? 1 : 0.85)};
    }
`;

export const CancelButton = styled.button`
    width: 100%;
    padding: 13px 20px;
    border: 1.5px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: #666;
    background-color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active {
        background-color: #f5f5f5;
    }
`;

export const ConfirmButton = styled.button<{ $color: string; $disabled: boolean }>`
    width: 100%;
    padding: 16px 20px;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    background-color: ${({ $color, $disabled }) => ($disabled ? '#bbb' : $color)};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: opacity 0.2s;
    transform: none;

    &:active {
        transform: none;
    }
`;
